import React, { useState, useEffect, useRef } from 'react';
import { Button, Card, Typography, Space, Statistic, Divider, Alert, message, Modal, Progress } from 'antd';
import { CloudSyncOutlined, InfoCircleOutlined, CodeOutlined, WarningOutlined, RocketOutlined } from '@ant-design/icons';
import firebase from '../../../firebase';

const { Title, Paragraph, Text } = Typography;

const OrderMigration = ({ db }) => {
    const [orderCount, setOrderCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [migrating, setMigrating] = useState(false);
    const [progress, setProgress] = useState(0);
    const [paused, setPaused] = useState(false);
    const [stopped, setStopped] = useState(false);
    const pausedRef = useRef(false);
    const stoppedRef = useRef(false);

    useEffect(() => { pausedRef.current = paused; }, [paused]);
    useEffect(() => { stoppedRef.current = stopped; }, [stopped]);

    const waitIfPaused = async () => {
        while (pausedRef.current && !stoppedRef.current) {
            await new Promise(resolve => setTimeout(resolve, 500));
        }
    };

    const fetchOrderCount = async () => {
        if (!db) return;
        setLoading(true);
        try {
            const snapshot = await db.collection('orders').get();
            setOrderCount(snapshot.size);
        } catch (error) {
            console.error("Error fetching order count:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrderCount();
    }, []);

    const performMigration = async () => {
        setMigrating(true);
        setPaused(false);
        setStopped(false);
        pausedRef.current = false;
        stoppedRef.current = false;
        setProgress(0);

        try {
            const migrateFunc = firebase.app().functions('asia-northeast1').httpsCallable('migrateOrdersFromProd');
            let hasMore = true;
            let lastId = null;
            let totalProcessed = 0;
            let totalToMigrate = 0;

            while (hasMore) {
                if (stoppedRef.current) break;
                await waitIfPaused();
                if (stoppedRef.current) break;

                const result = await migrateFunc({ limit: 500, lastId });
                
                if (result.data.success) {
                    if (totalToMigrate === 0 && result.data.total) {
                        totalToMigrate = result.data.total;
                    }

                    totalProcessed += result.data.count;
                    lastId = result.data.lastId;
                    hasMore = result.data.hasMore;
                    
                    if (totalToMigrate > 0) {
                        setProgress(Math.round((totalProcessed / totalToMigrate) * 100));
                    }
                    
                    // Update local count to show progress
                    fetchOrderCount();
                    
                    if (!hasMore) {
                        message.success(`Migration completed! Total migrated in this session: ${totalProcessed}`);
                    }
                } else {
                    message.warning(result.data.message);
                    hasMore = false;
                }
            }
        } catch (error) {
            console.error("Cloud Migration error:", error);
            message.error("Migration failed: " + error.message);
        } finally {
            setMigrating(false);
            if (stoppedRef.current) {
                message.warning("Migration stopped by user.");
            }
        }
    };

    const handleCloudMigrate = () => {
        Modal.confirm({
            title: 'Confirm Migration via Cloud Function',
            content: 'This will trigger the server-side migration in batches. The process will continue automatically until all orders are migrated.',
            onOk: () => {
                performMigration(); // Start migration without awaiting, so modal closes immediately
            }
        });
    };

    return (
        <Card title={<Title level={4}><CloudSyncOutlined /> Order Migration from Production</Title>}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Alert
                    message="Sequential Browser-Triggered Migration"
                    description="Trigger the migration directly from this dashboard. It now supports pause, resume, and real-time progress tracking."
                    type="success"
                    showIcon
                />

                <Card type="inner" title={<span><RocketOutlined /> Option 1: Browser-Triggered Migration (Recommended)</span>}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Paragraph>
                            This uses a Cloud Function to securely perform the migration on the server in batches.
                        </Paragraph>
                        <Alert
                            message="Prerequisite"
                            description={
                                <span>
                                    You must deploy the updated function first by running: <br />
                                    <Text code>firebase deploy --only functions</Text>
                                </span>
                            }
                            type="info"
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
                            <Statistic 
                                title="Current Local Orders" 
                                value={orderCount} 
                                loading={loading}
                            />
                            <Space>
                                {migrating ? (
                                    <>
                                        <Button onClick={() => setPaused(!paused)}>{paused ? "Resume" : "Pause"}</Button>
                                        <Button danger onClick={() => { setStopped(true); stoppedRef.current = true; }}>Stop</Button>
                                    </>
                                ) : (
                                    <Button 
                                        type="primary" 
                                        icon={<CloudSyncOutlined />}
                                        onClick={handleCloudMigrate}
                                        loading={migrating}
                                        disabled={loading}
                                        size="large"
                                    >
                                        Run Migration
                                    </Button>
                                )}
                            </Space>
                        </div>
                        {migrating && (
                            <div style={{ marginTop: 16 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                    <Text type="secondary">Migration Progress:</Text>
                                    <Text strong>{progress}%</Text>
                                </div>
                                <Progress percent={progress} status={paused ? "active" : "normal"} />
                            </div>
                        )}
                    </Space>
                </Card>

                <Divider />

                <Card type="inner" title={<span><CodeOutlined /> Option 2: Terminal Migration (Backup)</span>}>
                    <Paragraph>
                        If you prefer not to deploy a Cloud Function, you can still run the script manually:
                    </Paragraph>
                    <Card size="small" style={{ backgroundColor: '#f5f5f5', marginBottom: 12 }}>
                        <Text code style={{ fontSize: '14px' }}>node src/components/Admin/Migrate/migrate_orders.js</Text>
                    </Card>
                    <Paragraph type="secondary" size="small">
                        This uses <Text code>p4cloud-prod.json</Text> for full administrative access.
                    </Paragraph>
                </Card>

                <Alert
                    message="Important"
                    description="The service account guarantees bypass of Firestore security rules. Both options perform the same data sync."
                    type="warning"
                />
            </Space>
        </Card>
    );
};

export default OrderMigration;
